/**
 * Daily P&L Chart
 * Visualizes daily trading performance over time
 */

import { useEffect, useState } from 'react';
import { fetchDailyPerformance, type DailyPerformance } from '../services/forexService';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { safeFormatTimestamp } from '../lib/accountFormatters';

export function DailyPnLChart() {
  const [dailyData, setDailyData] = useState<DailyPerformance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'pnl' | 'winrate'>('pnl');

  useEffect(() => {
    const loadDailyData = async () => {
      try {
        const data = await fetchDailyPerformance(14); // Last 14 days
        setDailyData(data);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load performance data');
      } finally {
        setLoading(false);
      }
    };

    loadDailyData();
    const interval = setInterval(loadDailyData, 300000); // Refresh every 5 minutes
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="rounded-lg border border-neutral-800 bg-neutral-900 p-4">
        <div className="animate-pulse">
          <div className="h-6 w-32 bg-neutral-800 rounded mb-2"></div>
          <div className="h-64 w-full bg-neutral-800 rounded"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-900 bg-red-950/20 p-4">
        <div className="text-red-400 text-sm">{error}</div>
      </div>
    );
  }

  if (dailyData.length === 0) {
    return (
      <div className="rounded-lg border border-neutral-800 bg-neutral-900 p-4">
        <h3 className="text-sm font-medium text-neutral-400 mb-3">Daily Performance</h3>
        <div className="text-neutral-500 text-sm text-center py-8">
          No performance data available yet
        </div>
      </div>
    );
  }

  // Calculate cumulative P&L
  let cumulative = 0;
  const chartData = dailyData.map((day) => {
    cumulative += day.total_pnl;
    return {
      date: safeFormatTimestamp(day.date, { mode: 'date', fallback: '—', invalidFallback: '—' }),
      pnl: day.total_pnl,
      cumulative: cumulative,
      winRate: (day.win_rate || 0) * 100,
      trades: day.trades,
    };
  });

  // Calculate summary stats
  const totalPnL = dailyData.reduce((sum, day) => sum + day.total_pnl, 0);
  const totalTrades = dailyData.reduce((sum, day) => sum + day.trades, 0);
  const totalWins = dailyData.reduce((sum, day) => sum + day.wins, 0);
  const overallWinRate = totalTrades > 0 ? (totalWins / totalTrades) * 100 : 0;
  const avgDailyPnL = dailyData.length > 0 ? totalPnL / dailyData.length : 0;

  return (
    <div className="rounded-lg border border-neutral-800 bg-neutral-900 p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-neutral-400">Daily Performance</h3>
        <div className="flex gap-2">
          <button
            onClick={() => setViewMode('pnl')}
            className={`px-3 py-1 text-xs rounded ${
              viewMode === 'pnl'
                ? 'bg-blue-600 text-white'
                : 'bg-neutral-800 text-neutral-400 hover:bg-neutral-700'
            }`}
          >
            P&L
          </button>
          <button
            onClick={() => setViewMode('winrate')}
            className={`px-3 py-1 text-xs rounded ${
              viewMode === 'winrate'
                ? 'bg-blue-600 text-white'
                : 'bg-neutral-800 text-neutral-400 hover:bg-neutral-700'
            }`}
          >
            Win Rate
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-4 gap-3">
        <div className="rounded bg-neutral-800/50 p-2">
          <div className="text-[10px] text-neutral-500">Total P&L</div>
          <div className={`text-sm font-semibold ${totalPnL >= 0 ? 'text-green-400' : 'text-red-400'}`}>
            ${totalPnL.toFixed(2)}
          </div>
        </div>
        <div className="rounded bg-neutral-800/50 p-2">
          <div className="text-[10px] text-neutral-500">Avg Daily</div>
          <div className={`text-sm font-semibold ${avgDailyPnL >= 0 ? 'text-green-400' : 'text-red-400'}`}>
            ${avgDailyPnL.toFixed(2)}
          </div>
        </div>
        <div className="rounded bg-neutral-800/50 p-2">
          <div className="text-[10px] text-neutral-500">Win Rate</div>
          <div className="text-sm font-semibold text-neutral-300">{overallWinRate.toFixed(1)}%</div>
        </div>
        <div className="rounded bg-neutral-800/50 p-2">
          <div className="text-[10px] text-neutral-500">Total Trades</div>
          <div className="text-sm font-semibold text-neutral-300">{totalTrades}</div>
        </div>
      </div>

      {/* Chart */}
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          {viewMode === 'pnl' ? (
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#333" />
              <XAxis dataKey="date" stroke="#666" style={{ fontSize: 10 }} />
              <YAxis stroke="#666" style={{ fontSize: 10 }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1a1a1a',
                  border: '1px solid #333',
                  borderRadius: '4px',
                  fontSize: 12,
                }}
              />
              <Line
                type="monotone"
                dataKey="cumulative"
                stroke="#3b82f6"
                strokeWidth={2}
                dot={{ fill: '#3b82f6', r: 3 }}
                name="Cumulative P&L"
              />
            </LineChart>
          ) : (
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#333" />
              <XAxis dataKey="date" stroke="#666" style={{ fontSize: 10 }} />
              <YAxis stroke="#666" style={{ fontSize: 10 }} domain={[0, 100]} />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1a1a1a',
                  border: '1px solid #333',
                  borderRadius: '4px',
                  fontSize: 12,
                }}
              />
              <Bar dataKey="winRate" fill="#10b981" name="Win Rate %" />
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
}
